import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import NiaChat from "@/components/NiaChat";
import { Users, User, ArrowRight, FileSearch, FilePlus, ScrollText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field, Txt } from "@/pages/intake/_shared";

export default function StartNew() {
  const navigate = useNavigate();
  const [hasExistingTrust, setHasExistingTrust] = useState(null); // null | true | false
  const [trustType, setTrustType] = useState("single");
  const [trustName, setTrustName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [refInfo, setRefInfo] = useState(null); // { rep_code, rep_name }

  // Capture ?ref= from URL (or localStorage stash from earlier landing) and
  // resolve it to a display name via the public endpoint.
  useEffect(() => {
    let code = null;
    try {
      const params = new URLSearchParams(window.location.search);
      code = (params.get("ref") || "").trim().toUpperCase();
      if (code) localStorage.setItem("igc_ref_code", code);
      else code = (localStorage.getItem("igc_ref_code") || "").trim().toUpperCase();
    } catch (_) { /* SSR / private mode safe */ }
    if (!code) return;
    (async () => {
      try {
        const { data } = await api.get(`/reps/resolve/${encodeURIComponent(code)}`);
        setRefInfo({ rep_code: data.rep_code, rep_name: data.rep_name });
      } catch (_) { /* silent — invalid code, stay quiet */ }
    })();
  }, []);

  const create = async () => {
    if (hasExistingTrust === null) {
      toast.error("Please answer the question above to continue.");
      return;
    }
    const name = (trustName || "").trim();
    if (name.length < 3) {
      toast.error("Please enter the trust name.");
      return;
    }
    const phone = (clientPhone || "").replace(/\D/g, "");
    if (phone.length < 7) {
      toast.error("Please enter a phone number we can reach you at.");
      return;
    }
    setSubmitting(true);
    try {
      const intake_path = hasExistingTrust ? "review" : "new";
      const payload = { intake_path, trust_name: name, client_phone: clientPhone.trim() };
      payload.trust_type = hasExistingTrust ? "single" : trustType;
      if (refInfo?.rep_code) payload.rep_code = refInfo.rep_code;
      const { data } = await api.post("/trust-cases", payload);
      navigate(`/intake/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <h1 className="font-serif text-3xl sm:text-4xl text-navy mb-2">Start a new trust intake</h1>
      <p className="text-[#64748B] mb-8 max-w-2xl">
        First, tell us whether you already have a trust. We'll guide you through the right path.
      </p>

      <div className="bg-white border border-[#E2E0DA] rounded-xl p-5 sm:p-8 max-w-2xl">
        {/* Referral banner — shown only when a valid ?ref=CODE is present */}
        {refInfo && (
          <div
            className="mb-6 flex items-center gap-3 rounded-lg border border-bronze/40 bg-bronze/5 px-4 py-3 text-sm text-navy"
            data-testid="referral-banner">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-bronze/20 text-bronze font-bold">
              ★
            </span>
            <div>
              <div className="font-semibold">Referred by {refInfo.rep_name}</div>
              <div className="text-xs text-[#64748B]">
                Code: <span className="font-mono">{refInfo.rep_code}</span>
              </div>
            </div>
          </div>
        )}

        {/* ========== Step 1: Existing trust question ========== */}
        <p className="text-xs uppercase tracking-wide text-[#64748B] font-semibold mb-3">1. Tell us about your trust</p>
        <p className="font-serif text-xl sm:text-2xl text-navy mb-4">Do you currently have a living trust set up?</p>

        <div className="grid sm:grid-cols-2 gap-3 mb-2">
          <button
            type="button"
            onClick={() => setHasExistingTrust(false)}
            className={`text-left border rounded-xl p-4 sm:p-5 transition-all ${
              hasExistingTrust === false ? "border-bronze bg-bronze/5 ring-2 ring-bronze/30" : "border-[#E2E0DA] hover:border-bronze"
            }`}
            data-testid="has-existing-trust-no">
            <div className="h-10 w-10 rounded-full bg-sand-200 flex items-center justify-center mb-3">
              <FilePlus className="h-5 w-5 text-navy" />
            </div>
            <h3 className="font-serif text-xl text-navy">No</h3>
            <p className="text-sm text-[#64748B] mt-1">I want to create a new trust.</p>
          </button>
          <button
            type="button"
            onClick={() => setHasExistingTrust(true)}
            className={`text-left border rounded-xl p-4 sm:p-5 transition-all ${
              hasExistingTrust === true ? "border-bronze bg-bronze/5 ring-2 ring-bronze/30" : "border-[#E2E0DA] hover:border-bronze"
            }`}
            data-testid="has-existing-trust-yes">
            <div className="h-10 w-10 rounded-full bg-sand-200 flex items-center justify-center mb-3">
              <FileSearch className="h-5 w-5 text-navy" />
            </div>
            <h3 className="font-serif text-xl text-navy">Yes</h3>
            <p className="text-sm text-[#64748B] mt-1">I already have a trust I'd like reviewed.</p>
          </button>
        </div>

        {/* Path-specific note (no pricing — pricing is set by admin at payment time) */}
        {hasExistingTrust === true && (
          <div className="mt-4 mb-2 rounded-lg border-2 border-bronze/40 bg-bronze/5 p-4" data-testid="review-path-note">
            <p className="font-serif text-lg text-navy mb-1">Existing Trust Review</p>
            <p className="text-sm text-[#334155] leading-relaxed">
              Your existing trust will be reviewed by our legal team at no additional charge. Your enrollment also
              covers our free credit repair program and your 90-day cash settlement process. Upload your trust on
              the next step and we'll take it from there.
            </p>
          </div>
        )}
        {hasExistingTrust === false && (
          <div className="mt-4 mb-2 rounded-lg border border-[#E2E0DA] bg-sand-200/40 p-4" data-testid="new-path-note">
            <p className="font-serif text-lg text-navy mb-1">New Trust Drafting</p>
            <p className="text-sm text-[#334155] leading-relaxed">
              We'll guide you through an 8-step intake so our team can draft your new trust. You'll review the draft
              before any payment is collected.
            </p>
          </div>
        )}

        {/* ========== Step 2: Trust type (NEW path only) ========== */}
        {hasExistingTrust === false && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-wide text-[#64748B] font-semibold mb-3">2. Choose trust type</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setTrustType("single")}
                className={`text-left border rounded-xl p-4 sm:p-5 transition-all ${
                  trustType === "single" ? "border-bronze bg-bronze/5 ring-2 ring-bronze/30" : "border-[#E2E0DA] hover:border-bronze"
                }`}
                data-testid="new-trust-single">
                <div className="h-10 w-10 rounded-full bg-sand-200 flex items-center justify-center mb-3">
                  <User className="h-5 w-5 text-navy" />
                </div>
                <h3 className="font-serif text-xl text-navy">Single Trust</h3>
                <p className="text-sm text-[#64748B] mt-1">One grantor.</p>
              </button>
              <button
                type="button"
                onClick={() => setTrustType("joint")}
                className={`text-left border rounded-xl p-4 sm:p-5 transition-all ${
                  trustType === "joint" ? "border-bronze bg-bronze/5 ring-2 ring-bronze/30" : "border-[#E2E0DA] hover:border-bronze"
                }`}
                data-testid="new-trust-joint">
                <div className="h-10 w-10 rounded-full bg-sand-200 flex items-center justify-center mb-3">
                  <Users className="h-5 w-5 text-navy" />
                </div>
                <h3 className="font-serif text-xl text-navy">Joint Trust</h3>
                <p className="text-sm text-[#64748B] mt-1">Two grantors (spouses / partners).</p>
              </button>
            </div>
          </div>
        )}

        {/* ========== Step: Name your trust + phone ========== */}
        {hasExistingTrust !== null && (
          <div className="mt-6">
            <p className="text-xs uppercase tracking-wide text-[#64748B] font-semibold mb-3">
              {hasExistingTrust ? "2." : "3."} Your details
            </p>
            <Field id="trust-name" label="Trust name" required
              hint={
                hasExistingTrust
                  ? "Use the exact name on your existing trust document so we can match the records."
                  : 'Examples: "The John A. Smith Living Trust", "The Smith Family Revocable Trust".'
              }>
              <Txt id="trust-name" value={trustName} onChange={setTrustName} placeholder="The Smith Family Revocable Trust" testId="trust-name-input" />
            </Field>

            <div className="mt-4">
              <Field id="client-phone" label="Best phone number to reach you" required
                hint="We'll call from a 954 number. Standard rates may apply for SMS updates.">
                <input
                  id="client-phone"
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                  className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                  data-testid="client-phone-input"
                />
              </Field>
            </div>
          </div>
        )}

        {/* ========== Submit ========== */}
        <div className="mt-8 flex items-center justify-end">
          <button
            onClick={create}
            disabled={submitting || hasExistingTrust === null}
            className="btn-primary w-full sm:w-auto"
            data-testid="start-new-submit">
            {submitting ? "Starting…" : (hasExistingTrust ? "Continue to upload" : "Start intake")}
            <ArrowRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      </div>

      <div className="mt-6 max-w-2xl flex items-start gap-2 text-xs text-[#64748B]">
        <ScrollText className="h-4 w-4 shrink-0 mt-0.5" />
        <p>
          Both paths collect your contact information and route to your assigned agent. You can pause and return any time —
          progress is saved section by section.
        </p>
      </div>
      <NiaChat />
    </Layout>
  );
}
